import { RiMusic2Line, RiPlayFill, RiStopFill, RiShuffleLine, RiDownloadLine, RiLoader4Line } from '@remixicon/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { playMutationOptions, stopMutationOptions } from '@/lib/queries/playback'
import { shuffleSongsMutationOptions } from '@/lib/queries/songs'
import { usePlayback } from '@/lib/playback-context'
import type { Song } from './song-row-content'

export function PlaylistHero({
	name,
	icon,
	accentColor,
	songs,
	playlistId,
}: {
	name: string
	icon: string | null
	accentColor: string
	songs: Song[]
	playlistId: string
}) {
	const queryClient = useQueryClient()
	const { state: playbackState, download } = usePlayback()
	const playMutation = useMutation(playMutationOptions())
	const stopMutation = useMutation(stopMutationOptions())
	const shuffleMutation = useMutation(shuffleSongsMutationOptions(queryClient, playlistId))

	const isPlaying = playbackState.status === 'playing' && playbackState.playlistId === playlistId
	const isPending = playMutation.isPending || stopMutation.isPending

	const songCount = songs.length
	const downloadedCount = songs.filter((s) => s.file).length
	const allDownloaded = downloadedCount === songCount || songCount === 0
	const isDownloadingInThisPlaylist = download.songId && download.playlistId === playlistId

	return (
		<div className="pb-6 pt-12">
			<div className="flex items-center gap-5">
				<div
					className="flex size-20 shrink-0 items-center justify-center rounded-2xl shadow-lg"
					style={{
						backgroundColor: `color-mix(in oklch, ${accentColor} 15%, oklch(0.18 0 0))`,
						boxShadow: `0 8px 32px color-mix(in oklch, ${accentColor} 12%, transparent)`,
					}}
				>
					{icon ? (
						<span className="text-3xl leading-none">{icon}</span>
					) : (
						<RiMusic2Line className="size-8" style={{ color: accentColor }} />
					)}
				</div>

				<div className="flex-1">
					<h2 className="font-serif text-3xl font-semibold tracking-tight">
						{name}
					</h2>
					<div className="mt-1 flex items-center gap-3">
						<span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
							{songCount} {songCount === 1 ? 'song' : 'songs'}
						</span>
						{!allDownloaded && (
							<>
								<span className="text-muted-foreground/20">·</span>
								<span className="flex items-center gap-1.5 text-xs text-amber-500/70">
									<RiDownloadLine className="size-3" />
									{downloadedCount}/{songCount}
								</span>
							</>
						)}
					</div>
				</div>

				<div className="flex items-center gap-1">
					<Button
						variant="ghost"
						size="icon"
						disabled={songCount === 0 || shuffleMutation.isPending}
						onClick={() => shuffleMutation.mutate()}
						className="text-muted-foreground hover:text-foreground"
					>
						<RiShuffleLine className="size-4" />
					</Button>
					<Button
						variant="ghost"
						size="icon"
						disabled={songCount === 0 || isPending}
						onClick={() => {
							if (isPlaying) {
								stopMutation.mutate()
							} else {
								playMutation.mutate({ playlistId })
							}
						}}
						style={{ color: accentColor }}
					>
						{isPlaying ? (
							<RiStopFill className="size-5" />
						) : (
							<RiPlayFill className="size-5" />
						)}
					</Button>
				</div>
			</div>

			{/* Currently downloading indicator */}
			{isDownloadingInThisPlaylist && (
				<div className="mt-4 flex items-center gap-2.5 rounded-lg bg-amber-500/5 px-3 py-2.5 ring-1 ring-amber-500/10">
					<RiLoader4Line className="size-3.5 shrink-0 animate-spin text-amber-500/60" />
					<span className="truncate text-xs text-amber-500/70">
						Downloading <span className="font-medium text-amber-500/90">{download.songName}</span>
						{download.songArtist && (
							<span className="text-amber-500/50"> by {download.songArtist}</span>
						)}
					</span>
				</div>
			)}
		</div>
	)
}
